import React, { useState, useRef } from 'react';
import type { Channel } from '../services/channelService';
import channelService from '../services/channelService';
import ChannelPlayerModal from './ChannelPlayerModal';

interface ChannelDetailModalProps {
  channel: Channel | null;
  onClose: () => void;
  onEdit?: (channel: Channel) => void;
  onDelete?: (channel: Channel) => Promise<void>;
  onStart?: (channel: Channel) => Promise<void>;
  onStop?: (channel: Channel) => Promise<void>;
  onRefresh?: () => void;
}

const ChannelDetailModal: React.FC<ChannelDetailModalProps> = ({
  channel,
  onClose,
  onEdit,
  onDelete,
  onStart,
  onStop,
  onRefresh,
}) => {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<'start' | 'stop' | 'delete' | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  if (!channel) return null;

  // Check if stream URL is available
  const hasStreamUrl = channel.hls_master_url || channel.hls_720p_url || channel.hls_1080p_url;

  // Handle logo upload
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Please upload a PNG, JPEG, or WebP image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    setLogoError(null);

    try {
      await channelService.uploadLogo(channel.id, file);
      // Refresh channel data
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      setLogoError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  // Handle start with loading
  const handleStart = async () => {
    if (!onStart || actionLoading) return;
    setActionLoading('start');
    try {
      await onStart(channel);
      // Close modal after successful start
      setTimeout(() => onClose(), 500);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle stop with loading
  const handleStop = async () => {
    if (!onStop || actionLoading) return;
    setActionLoading('stop');
    try {
      await onStop(channel);
      // Close modal after successful stop
      setTimeout(() => onClose(), 500);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle delete with loading
  const handleDelete = async () => {
    if (!onDelete || actionLoading) return;
    setActionLoading('delete');
    try {
      await onDelete(channel);
    } finally {
      setActionLoading(null);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = (url: string, label: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(label);
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  // Format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format watch time
  const formatWatchTime = (interval: string): string => {
    if (interval === '0' || !interval) return '0 hours';
    const match = interval.match(/(\d+):(\d+):(\d+)/);
    if (match) {
      const hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      if (hours > 0) return `${hours} hours ${minutes} minutes`;
      return `${minutes} minutes`;
    }
    return interval;
  };

  // Status colors
  const statusColors = {
    stopped: 'bg-gray-100 text-gray-800',
    starting: 'bg-blue-100 text-blue-800',
    running: 'bg-green-100 text-green-800',
    stopping: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex-1 pr-4">
            <h2 className="text-2xl font-bold text-gray-900 truncate">{channel.name}</h2>
            {channel.description && (
              <p className="text-sm text-gray-600 mt-1">{channel.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                statusColors[channel.status]
              }`}
            >
              {channel.status.toUpperCase()}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Status Message */}
          {channel.status_message && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">{channel.status_message}</p>
            </div>
          )}

          {/* Quick Stats */}
          {channel.status === 'running' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">Current Viewers</p>
                <p className="text-3xl font-bold text-green-900 mt-1">
                  {channel.current_viewers.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">Total Watch Time</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">
                  {formatWatchTime(channel.total_watch_time)}
                </p>
              </div>
            </div>
          )}

          {/* Stream URLs */}
          {(channel.hls_master_url || channel.hls_720p_url || channel.hls_1080p_url) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Stream URLs</h3>
              <div className="space-y-2">
                {channel.hls_master_url && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      HLS Master Playlist
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={channel.hls_master_url}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(channel.hls_master_url!, 'master')}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                      >
                        {copiedUrl === 'master' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                {channel.hls_720p_url && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      720p Stream
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={channel.hls_720p_url}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(channel.hls_720p_url!, '720p')}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                      >
                        {copiedUrl === '720p' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                {channel.hls_1080p_url && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      1080p Stream
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={channel.hls_1080p_url}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(channel.hls_1080p_url!, '1080p')}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                      >
                        {copiedUrl === '1080p' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Output Settings */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
                Output Settings
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Format:</span>
                  <span className="font-medium text-gray-900">
                    {channel.output_format.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Resolution:</span>
                  <span className="font-medium text-gray-900">
                    {channel.output_resolution.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Preset:</span>
                  <span className="font-medium text-gray-900">{channel.transcode_preset}</span>
                </div>
              </div>
            </div>

            {/* HLS Settings */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                HLS Settings
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Segment Duration:</span>
                  <span className="font-medium text-gray-900">
                    {channel.hls_segment_duration}s
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Playlist Length:</span>
                  <span className="font-medium text-gray-900">
                    {channel.hls_playlist_length} segments
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">DVR Window:</span>
                  <span className="font-medium text-gray-900">
                    {channel.dvr_window > 0
                      ? `${Math.floor(channel.dvr_window / 60)} min`
                      : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Low Latency:</span>
                  <span
                    className={`font-medium ${
                      channel.low_latency_mode ? 'text-green-600' : 'text-gray-900'
                    }`}
                  >
                    {channel.low_latency_mode ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* RTMP Settings */}
            {(channel.output_format === 'rtmp' || channel.output_format === 'both') && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  RTMP Settings
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">URL:</span>
                    <p className="font-mono text-xs text-gray-900 break-all mt-1">
                      {channel.rtmp_url || 'Not configured'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Stream Key:</span>
                    <p className="font-mono text-xs text-gray-900 mt-1">
                      {channel.rtmp_key ? '••••••••••••' : 'Not configured'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Branding */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
                Branding
              </h3>
              <div className="space-y-3 text-sm">
                {/* Logo Upload Section */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Logo:</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${channel.logo_path ? 'text-green-600' : 'text-gray-900'}`}>
                      {channel.logo_path ? 'Configured' : 'None'}
                    </span>
                    <label
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors inline-flex items-center gap-1 ${
                        uploadingLogo
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer'
                      }`}
                    >
                      {uploadingLogo ? (
                        <>
                          <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Uploading...
                        </>
                      ) : channel.logo_path ? (
                        'Change'
                      ) : (
                        'Upload'
                      )}
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="absolute w-0 h-0 opacity-0 overflow-hidden"
                        style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }}
                      />
                    </label>
                  </div>
                </div>
                {logoError && (
                  <p className="text-xs text-red-600 mt-1">{logoError}</p>
                )}
                {channel.logo_path && (
                  <p className="text-xs text-gray-500">
                    Restart channel to apply logo changes to stream
                  </p>
                )}
                {/* Logo Preview Thumbnail */}
                {channel.logo_path && (
                  <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-16 h-16 bg-white rounded border border-gray-200 flex items-center justify-center overflow-hidden">
                      <img
                        src={channel.logo_path.startsWith('/') ? `http://control.prolivestream.com${channel.logo_path}` : channel.logo_path}
                        alt="Channel Logo"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-700">Logo Preview</p>
                      <p className="text-xs text-gray-500 capitalize">
                        Position: {channel.logo_position.replace('-', ' ')}
                      </p>
                    </div>
                  </div>
                )}
                {channel.logo_path && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Position:</span>
                    <span className="font-medium text-gray-900 capitalize">
                      {channel.logo_position.replace('-', ' ')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Now Playing:</span>
                  <span
                    className={`font-medium ${
                      channel.show_now_playing ? 'text-green-600' : 'text-gray-900'
                    }`}
                  >
                    {channel.show_now_playing ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Up Next:</span>
                  <span
                    className={`font-medium ${
                      channel.show_up_next ? 'text-green-600' : 'text-gray-900'
                    }`}
                  >
                    {channel.show_up_next ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Ad Break & Slate Settings */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0h8v12H6V4z" clipRule="evenodd" />
              </svg>
              Ad Break & Slate Settings
            </h3>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Ad Interval:</span>
                  <span className="font-medium text-gray-900">
                    {Math.floor((channel.ad_interval || 300) / 60)} min
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Ad Duration:</span>
                  <span className="font-medium text-gray-900">
                    {channel.ad_duration || 30}s
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">HLS Bitrate Config:</span>
                <span className="font-medium text-gray-900">
                  {channel.hls_bitrate_config === 'amagi' && 'Amagi Config'}
                  {channel.hls_bitrate_config === '720p' && '720p Only'}
                  {channel.hls_bitrate_config === '1080p' && '1080p Only'}
                  {channel.hls_bitrate_config === '720p_1080p' && '720p + 1080p'}
                  {!channel.hls_bitrate_config && '720p (Default)'}
                </span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <div>
                  <span className="text-gray-600">Loop Last Template:</span>
                  <p className="text-xs text-gray-500 mt-1">Continue playing last scheduled template when no new programs</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={channel.loop_last_template || false}
                    onChange={async (e) => {
                      try {
                        await channelService.updateChannel(channel.id, {
                          loop_last_template: e.target.checked
                        });
                        // Refresh channel data
                        if (onRefresh) {
                          onRefresh();
                        }
                      } catch (err: any) {
                        alert(err.response?.data?.message || 'Failed to update setting');
                        e.target.checked = !e.target.checked;
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* YouTube Push Settings (only show if RTMP is enabled) */}
              {channel.rtmp_url && (
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-gray-600 font-medium">Push to YouTube:</span>
                      <p className="text-xs text-gray-500">Stream directly to YouTube Live</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={channel.push_to_youtube || false}
                        onChange={async (e) => {
                          try {
                            await channelService.updateChannel(channel.id, {
                              push_to_youtube: e.target.checked
                            });
                            // Refresh channel data
                            if (onRefresh) {
                              onRefresh();
                            }
                          } catch (err: any) {
                            alert(err.response?.data?.message || 'Failed to update YouTube push setting');
                            e.target.checked = !e.target.checked;
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>
                  
                  {channel.push_to_youtube && (
                    <>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          YouTube Stream Key:
                        </label>
                        <input
                          type="password"
                          defaultValue={channel.youtube_stream_key || ''}
                          onBlur={async (e) => {
                            if (e.target.value !== channel.youtube_stream_key) {
                              try {
                                await channelService.updateChannel(channel.id, {
                                  youtube_stream_key: e.target.value
                                });
                                if (onRefresh) {
                                  onRefresh();
                                }
                              } catch (err: any) {
                                alert(err.response?.data?.message || 'Failed to update YouTube stream key');
                              }
                            }
                          }}
                          placeholder="Enter your YouTube stream key"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Get this from YouTube Studio → Go Live → Stream Settings
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">YouTube Push Status:</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                            channel.youtube_push_status === 'running' 
                              ? 'bg-green-100 text-green-800' 
                              : channel.youtube_push_status === 'starting' || channel.youtube_push_status === 'stopping'
                              ? 'bg-yellow-100 text-yellow-800'
                              : channel.youtube_push_status === 'error'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {channel.youtube_push_status === 'running' && (
                              <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                <circle cx="10" cy="10" r="8" />
                              </svg>
                            )}
                            {channel.youtube_push_status === 'starting' || channel.youtube_push_status === 'stopping' ? (
                              <svg className="w-3 h-3 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                              </svg>
                            ) : null}
                            {channel.youtube_push_status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Slate Preview */}
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Slate Video:</span>
                  <span className={`font-medium ${channel.slate_path ? 'text-green-600' : 'text-gray-500'}`}>
                    {channel.slate_path ? 'Custom' : 'Default (We\'ll be right back)'}
                  </span>
                </div>
                {channel.slate_path ? (
                  <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-24 h-14 bg-gray-800 rounded border border-gray-300 flex items-center justify-center overflow-hidden relative">
                      <video
                        src={channel.slate_path.startsWith('/') ? `http://control.prolivestream.com${channel.slate_path}` : channel.slate_path}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                        onLoadedMetadata={(e) => {
                          (e.target as HTMLVideoElement).currentTime = 1;
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-700">Custom Slate</p>
                      <p className="text-xs text-gray-500">Shows during ad breaks</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                    <div className="w-24 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded border border-blue-300 flex items-center justify-center">
                      <span className="text-white text-xs font-medium text-center px-1">We'll be<br/>right back</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-blue-700">Default Slate</p>
                      <p className="text-xs text-blue-600">Animated "We'll be right back" screen</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Timeline</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Created:</span>
                <p className="font-medium text-gray-900 mt-1">{formatDate(channel.created_at)}</p>
              </div>
              <div>
                <span className="text-gray-600">Last Updated:</span>
                <p className="font-medium text-gray-900 mt-1">{formatDate(channel.updated_at)}</p>
              </div>
              {channel.started_at && (
                <div>
                  <span className="text-gray-600">Started:</span>
                  <p className="font-medium text-gray-900 mt-1">
                    {formatDate(channel.started_at)}
                  </p>
                </div>
              )}
              {channel.stopped_at && (
                <div>
                  <span className="text-gray-600">Stopped:</span>
                  <p className="font-medium text-gray-900 mt-1">
                    {formatDate(channel.stopped_at)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Engine Assignment */}
          {channel.engine_id && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-900">Playout Engine Assigned</p>
                  <p className="text-xs text-blue-700 mt-0.5">ID: {channel.engine_id}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Test Player button */}
            {hasStreamUrl && (
              <button
                onClick={() => setShowPlayer(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                Test Player
              </button>
            )}

            {/* Delete button */}
            {channel.status === 'stopped' && onDelete && (
              <button
                onClick={handleDelete}
                disabled={actionLoading !== null}
                className={`px-4 py-2 border rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                  actionLoading === 'delete'
                    ? 'bg-red-100 text-red-400 border-red-200 cursor-not-allowed'
                    : 'text-red-600 hover:bg-red-50 border-red-300'
                }`}
              >
                {actionLoading === 'delete' && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete Channel'}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Start/Stop button */}
            {(channel.status === 'stopped' || channel.status === 'error') && onStart && (
              <button
                onClick={handleStart}
                disabled={actionLoading !== null}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                  actionLoading === 'start'
                    ? 'bg-green-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white`}
              >
                {actionLoading === 'start' && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {actionLoading === 'start' ? 'Starting...' : 'Start Channel'}
              </button>
            )}

            {channel.status === 'running' && onStop && (
              <button
                onClick={handleStop}
                disabled={actionLoading !== null}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                  actionLoading === 'stop'
                    ? 'bg-red-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                } text-white`}
              >
                {actionLoading === 'stop' && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {actionLoading === 'stop' ? 'Stopping...' : 'Stop Channel'}
              </button>
            )}

            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium text-sm transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Player Modal */}
      {showPlayer && (
        <ChannelPlayerModal
          channel={channel}
          onClose={() => setShowPlayer(false)}
        />
      )}
    </div>
  );
};

export default ChannelDetailModal;
